// Realization basis — the TIMING axis of a contribution value (#4008 closure item 6).
//
// THE DEFECT THIS CLOSES. PR #4080 Errata E3, confirmed against the engine: the
// bridge mixed bases. `workforceReductionNetEbitdaOpportunityUsd` names a
// NET-OF-SEVERANCE FIRST-YEAR number as the EBITDA contribution, while every other
// lever family publishes an undifferentiated recurring number. Summing them is an
// apples-to-oranges addition that no downstream consumer could detect, because
// nothing on the value carried its basis. Run live, the two bases give a gross
// five-lever stack of 89,993,791.31 (year-one workforce) vs 92,063,791.31
// (run-rate workforce) — a 2,070,000 swing on the headline gross that was
// invisible.
//
// PR #4080 Gap 3 prescribes exactly this file: "put runRateUsd / yearOneUsd /
// oneTimeCostUsd / fullRealizationDate on the shared lever-contribution contract and
// make bridges refuse to sum across bases. Owner: shared contribution contract
// (substrate), not any one module."
//
// THIS IS NOT THE OTHER "BASIS". Helix already has two unrelated concepts wearing
// that word, and this one is orthogonal to BOTH:
//   - `FlagshipBasisLensId` (basis-of-value table) = EVIDENCE STANDARD — how well
//     proven the dollar is (certified_standalone, deconflicted_floor, ...).
//   - `DecisionConsoleValueBasis` = VALUE COMPOSITION — gross fully loaded vs net
//     EBITDA vs approved value binding.
//   - THIS = REALIZATION TIMING — over what period the dollars actually land.
// A single lever carries all three at once. Never collapse them; never name a
// field bare `basis` in this axis.
//
// HONESTY RULE (the founder's constraint, enforced structurally below): where a
// module's data supports only one basis, it is LABELED as single-basis with a
// stated reason. The missing counterpart is NEVER synthesized — no annualizing a
// one-time number, no amortizing a run rate. `buildLeverContributionBases` cannot
// express an invented value: every entry must be handed a real governed number.

/**
 * The closed set of realization bases. Ordered from most-recurring to least.
 *
 * - `run_rate`      — the steady-state annual effect once the move is fully
 *                     implemented. Ongoing; excludes one-time costs to get there.
 * - `year_one_net`  — what actually lands in the first twelve months: the run-rate
 *                     effect net of the one-time costs incurred to achieve it.
 * - `one_time_cash` — a non-recurring cash movement. Never recurs, so it can never
 *                     be added to a recurring basis.
 */
export type RealizationBasis = 'run_rate' | 'year_one_net' | 'one_time_cash';

/** Iteration order for every consumer that enumerates bases. */
export const REALIZATION_BASIS_IDS: readonly RealizationBasis[] = Object.freeze([
  'run_rate',
  'year_one_net',
  'one_time_cash',
]);

/**
 * The default basis a bridge/value-summary renders when the answer does not pin
 * one. Run rate is the default because it is the only basis every recurring lever
 * family can state, and it is the basis an executive means by "annual EBITDA
 * contribution".
 */
export const DEFAULT_RENDER_REALIZATION_BASIS: RealizationBasis = 'run_rate';

export type RealizationBasisDescriptor = Readonly<{
  basis: RealizationBasis;
  /** Executive label. Lived words, no category jargon. */
  label: string;
  /** What the number means, in one sentence a CFO reads without a presenter. */
  definition: string;
  /** Whether the effect repeats in later years. */
  recurring: boolean;
}>;

export const REALIZATION_BASIS_REGISTRY: Readonly<
  Record<RealizationBasis, RealizationBasisDescriptor>
> = Object.freeze({
  run_rate: Object.freeze({
    basis: 'run_rate' as const,
    label: 'Run rate',
    definition:
      'The full-year effect once the move is finished and steady — what it is ' +
      'worth every year from then on. The one-time cost of getting there is not ' +
      'taken off here.',
    recurring: true,
  }),
  year_one_net: Object.freeze({
    basis: 'year_one_net' as const,
    label: 'First year, net',
    definition:
      'What actually lands in the first twelve months: the run-rate effect less ' +
      'the one-time cost paid to get it. Lower than run rate in year one, and it ' +
      'catches up to run rate afterwards.',
    recurring: false,
  }),
  one_time_cash: Object.freeze({
    basis: 'one_time_cash' as const,
    label: 'One-time cash',
    definition:
      'A single cash movement that does not repeat. It never belongs in a ' +
      'recurring total.',
    recurring: false,
  }),
});

/** One dollar figure stated on exactly ONE basis, carrying its own receipt. */
export type BasisQualifiedValueUsd = Readonly<{
  basis: RealizationBasis;
  valueUsd: number;
  /**
   * The numeric-binding key that carries this figure to the surface, or null when
   * the value is assembly-internal and rendered through another binding. A null
   * binding key is a rendering constraint, never a licence to invent the number.
   */
  bindingKey: string | null;
}>;

/**
 * A lever's contribution stated across every basis its governed data ACTUALLY
 * supports. Construct only via `buildLeverContributionBases` — the builder is what
 * enforces that a stated basis has a real number behind it.
 */
export type LeverContributionBases = Readonly<{
  leverId: string;
  /** One entry per basis the lever's data supports. Never empty. */
  values: readonly BasisQualifiedValueUsd[];
  /** The basis this lever's module considers authoritative for itself. */
  primaryBasis: RealizationBasis;
  /**
   * Honest label when the lever supports only one basis: WHY the others are
   * absent. Null when the lever states more than one basis. This is the field
   * that keeps a single-basis module honest instead of back-filled.
   */
  singleBasisReason: string | null;
}>;

function roundUsd(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Builds a lever's basis set from real governed numbers.
 *
 * Fail-closed: throws when a basis is declared with a non-finite value, when the
 * same basis is declared twice, when the set is empty, when the primary basis is
 * not among the stated values, or when a single-basis set omits its reason. A
 * caller that cannot supply a real number for a basis must OMIT the basis — that
 * absence is the honest signal, and the reason field is where it gets explained.
 */
export function buildLeverContributionBases(input: {
  leverId: string;
  values: readonly BasisQualifiedValueUsd[];
  primaryBasis: RealizationBasis;
  singleBasisReason?: string | null;
}): LeverContributionBases {
  const { leverId, values, primaryBasis } = input;
  if (values.length === 0) {
    throw new Error(
      `realization basis: lever "${leverId}" stated no basis. A lever with no ` +
        'governed value must be omitted, never given an empty basis set.',
    );
  }
  const seen = new Set<RealizationBasis>();
  for (const entry of values) {
    if (!REALIZATION_BASIS_IDS.includes(entry.basis)) {
      throw new Error(
        `realization basis: lever "${leverId}" stated unregistered basis ` +
          `"${entry.basis}". The vocabulary is closed.`,
      );
    }
    if (!Number.isFinite(entry.valueUsd)) {
      throw new Error(
        `realization basis: lever "${leverId}" stated basis "${entry.basis}" ` +
          'with a non-finite value. Omit the basis instead of inventing it.',
      );
    }
    if (seen.has(entry.basis)) {
      throw new Error(
        `realization basis: lever "${leverId}" stated basis "${entry.basis}" ` +
          'twice. One value per basis.',
      );
    }
    seen.add(entry.basis);
  }
  if (!seen.has(primaryBasis)) {
    throw new Error(
      `realization basis: lever "${leverId}" named primary basis ` +
        `"${primaryBasis}" but did not state a value on it.`,
    );
  }
  const singleBasisReason = input.singleBasisReason ?? null;
  if (values.length === 1 && !singleBasisReason) {
    throw new Error(
      `realization basis: lever "${leverId}" states one basis and must say why ` +
        'the others are absent. Label it honestly rather than inventing the other.',
    );
  }
  return Object.freeze({
    leverId,
    values: Object.freeze(
      [...values]
        .sort(
          (a, b) =>
            REALIZATION_BASIS_IDS.indexOf(a.basis) -
            REALIZATION_BASIS_IDS.indexOf(b.basis),
        )
        .map((entry) =>
          Object.freeze({ ...entry, valueUsd: roundUsd(entry.valueUsd) }),
        ),
    ),
    primaryBasis,
    singleBasisReason: values.length === 1 ? singleBasisReason : null,
  });
}

/** The value a lever states on `basis`, or null when it states none. */
export function selectBasisValue(
  bases: LeverContributionBases,
  basis: RealizationBasis,
): BasisQualifiedValueUsd | null {
  return bases.values.find((entry) => entry.basis === basis) ?? null;
}

/**
 * Why a lever is absent from a single-basis total. Consumers render this instead
 * of silently dropping the lever — a lever missing from a total must always be
 * NAMED, per the render-open law.
 */
export type BasisAbsence = Readonly<{
  leverId: string;
  /** The basis the total is being taken on. */
  requestedBasis: RealizationBasis;
  /** The bases this lever does state. */
  statedBases: readonly RealizationBasis[];
  /** The module's own honest explanation, when it gave one. */
  reason: string | null;
}>;

/**
 * The verdict of taking a total across levers. `blocked` is the fence PR #4080
 * Gap 3 asked for: a bridge cannot sum across bases.
 */
export type BasisSummationVerdict =
  | Readonly<{
      status: 'summable';
      basis: RealizationBasis;
      totalUsd: number;
      /** Levers that contributed to the total. */
      includedLeverIds: readonly string[];
      /** Levers that could not, each with its reason. Never silently dropped. */
      absences: readonly BasisAbsence[];
    }>
  | Readonly<{
      status: 'blocked';
      reason: 'no_lever_states_basis';
      basis: RealizationBasis;
      absences: readonly BasisAbsence[];
    }>;

/**
 * Sums lever contributions on ONE basis. There is deliberately no function in
 * this module that sums across bases — the absence is the fence. Levers that do
 * not state the requested basis are excluded and NAMED in `absences`; they are
 * never coerced, annualized, or amortized onto it.
 */
export function sumOnSingleBasis(
  levers: readonly LeverContributionBases[],
  basis: RealizationBasis,
): BasisSummationVerdict {
  const includedLeverIds: string[] = [];
  const absences: BasisAbsence[] = [];
  let totalUsd = 0;
  for (const lever of levers) {
    const match = selectBasisValue(lever, basis);
    if (match) {
      totalUsd += match.valueUsd;
      includedLeverIds.push(lever.leverId);
      continue;
    }
    absences.push(
      Object.freeze({
        leverId: lever.leverId,
        requestedBasis: basis,
        statedBases: Object.freeze(lever.values.map((entry) => entry.basis)),
        reason: lever.singleBasisReason,
      }),
    );
  }
  if (includedLeverIds.length === 0) {
    return Object.freeze({
      status: 'blocked' as const,
      reason: 'no_lever_states_basis' as const,
      basis,
      absences: Object.freeze(absences),
    });
  }
  return Object.freeze({
    status: 'summable' as const,
    basis,
    totalUsd: roundUsd(totalUsd),
    includedLeverIds: Object.freeze(includedLeverIds),
    absences: Object.freeze(absences),
  });
}

/**
 * Detects the defect directly: given a set of levers each contributing its OWN
 * primary basis to a total (which is what the bridge did before this contract),
 * reports whether that total mixed bases. Used by contract tests and by the
 * bridge's own self-check.
 */
export function detectMixedBasisTotal(
  levers: readonly LeverContributionBases[],
): Readonly<{
  mixed: boolean;
  basesPresent: readonly RealizationBasis[];
}> {
  const present = new Set<RealizationBasis>();
  for (const lever of levers) present.add(lever.primaryBasis);
  const basesPresent = REALIZATION_BASIS_IDS.filter((basis) =>
    present.has(basis),
  );
  return Object.freeze({
    mixed: basesPresent.length > 1,
    basesPresent: Object.freeze(basesPresent),
  });
}

/**
 * The alternate-view descriptor a value summary renders beside its primary total:
 * the same levers, taken on a different basis, labeled. Null when no second basis
 * is stateable — in which case the surface says so rather than showing a toggle
 * that does nothing.
 */
export type BasisAlternateView = Readonly<{
  basis: RealizationBasis;
  label: string;
  definition: string;
  totalUsd: number;
  includedLeverIds: readonly string[];
  absences: readonly BasisAbsence[];
}>;

export function buildBasisAlternateView(
  levers: readonly LeverContributionBases[],
  basis: RealizationBasis,
): BasisAlternateView | null {
  const verdict = sumOnSingleBasis(levers, basis);
  if (verdict.status === 'blocked') return null;
  const descriptor = REALIZATION_BASIS_REGISTRY[basis];
  return Object.freeze({
    basis,
    label: descriptor.label,
    definition: descriptor.definition,
    totalUsd: verdict.totalUsd,
    includedLeverIds: verdict.includedLeverIds,
    absences: verdict.absences,
  });
}
